/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  View,
  StyleSheet,
  PanResponder,
  TouchableOpacity,
  Image,
  useWindowDimensions,
  Platform,
  Animated,
  ViewStyle,
} from 'react-native';
import { pd, getColor } from '@airasia-phoenix/react-native/utils/utils';
import { useStorage } from '../../hooks/useStorage';
import { useDeviceInfo } from '@airasia-rn/ui';

const ICON_SIZE = 60;
const EDGE_PADDING = 40;
const MOBILE_EDGE_HORIZONTAL = 16;
const MOBILE_EDGE_VERTICAL = 160;

type Quadrant = 'Q1' | 'Q2' | 'Q3' | 'Q4';

const VALID_QUADRANTS: Quadrant[] = ['Q1', 'Q2', 'Q3', 'Q4'];

const isValidQuadrant = (value: unknown): value is Quadrant => {
  return VALID_QUADRANTS.includes(value as Quadrant);
};

interface AskBoFloatingButtonProps {
  onPress?: () => void;
  onHover?: () => void;
  onTouchStart?: () => void;
}

export default function AskBoFloatingButton({
  onPress,
  onHover,
  onTouchStart,
}: AskBoFloatingButtonProps) {
  const { width, height } = useWindowDimensions();
  const { isMobileWidth } = useDeviceInfo();

  const pan = useRef(new Animated.ValueXY()).current;

  const dragPoint = useRef({ x: 0, y: 0 });
  const hasDragged = useRef(false);

  const [quadrant, setQuadrant] = useState<Quadrant>('Q4');
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const { getItem, setItem } = useStorage();

  useEffect(() => {
    const load = async () => {
      try {
        const saved = await getItem('askbo_quadrant');

        if (saved && isValidQuadrant(saved)) {
          setQuadrant(saved);
        }
      } catch (error) {
        console.error('Failed to load quadrant:', error);
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      typeof document === 'undefined' ||
      !document.body
    ) {
      return;
    }

    const container = document.createElement('div');
    container.setAttribute('data-testid', 'askbo-portal');
    document.body.appendChild(container);
    setPortalContainer(container);

    return () => {
      document.body.removeChild(container);
      setPortalContainer(null);
    };
  }, []);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => false,

      onMoveShouldSetPanResponder: (_, g) => {
        if (Math.abs(g.dx) > 4 || Math.abs(g.dy) > 4) {
          hasDragged.current = true;
          return true;
        }
        return false;
      },

      onPanResponderMove: (_, gesture) => {
        dragPoint.current = {
          x: gesture.moveX,
          y: gesture.moveY,
        };

        pan.setValue({
          x: gesture.dx,
          y: gesture.dy,
        });
      },

      onPanResponderRelease: async () => {
        const centerX = dragPoint.current.x;
        const centerY = dragPoint.current.y;

        const isLeft = centerX < width / 2;
        const isTop = centerY < height / 2;

        let key: Quadrant = 'Q4';

        if (isLeft && isTop) key = 'Q1';
        else if (!isLeft && isTop) key = 'Q2';
        else if (isLeft && !isTop) key = 'Q3';

        setQuadrant(key);

        await setItem('askbo_quadrant', key);

        pan.setValue({ x: 0, y: 0 });

        setTimeout(() => {
          hasDragged.current = false;
        }, 0);
      },
    }),
  ).current;

  const getPaddingStyle = (): ViewStyle => {
    const horizontalOffset = isMobileWidth
      ? MOBILE_EDGE_HORIZONTAL
      : EDGE_PADDING;
    const topOffset = isMobileWidth
      ? MOBILE_EDGE_VERTICAL + 60
      : EDGE_PADDING + 60;
    const bottomOffset = isMobileWidth ? MOBILE_EDGE_VERTICAL : EDGE_PADDING;

    switch (quadrant) {
      case 'Q1':
        return {
          top: topOffset,
          left: horizontalOffset,
        };

      case 'Q2':
        return {
          top: topOffset,
          right: horizontalOffset,
        };

      case 'Q3':
        return {
          bottom: bottomOffset,
          left: horizontalOffset,
        };

      default:
        return {
          bottom: bottomOffset,
          right: horizontalOffset,
        };
    }
  };

  const askBoButton = (
    <View style={[styles.container, getPaddingStyle()]}>
      <Animated.View
        {...panResponder.panHandlers}
        style={[
          styles.askbo,
          {
            transform: pan.getTranslateTransform(),
          },
        ]}
      >
        <TouchableOpacity
          testID="askbo-button"
          activeOpacity={0.9}
          style={styles.circle}
          accessibilityRole="button"
          accessibilityLabel="Ask Bo - Chat with our AI assistant"
          onPress={() => !hasDragged.current && onPress?.()}
          {...({
            onMouseEnter: onHover,
            onTouchStart: onTouchStart,
          } as Record<string, any>)}
        >
          <Image
            accessible
            accessibilityLabel="Ask Bo icon"
            source={{
              uri: 'https://static.airasia.com/design/images/askbo-blurb.webp',
            }}
            style={styles.icon}
          />
        </TouchableOpacity>
      </Animated.View>
    </View>
  );

  if (Platform.OS === 'web' && portalContainer) {
    return createPortal(askBoButton, portalContainer);
  }

  return askBoButton;
}

const styles = StyleSheet.create({
  container: {
    ...(Platform.select<ViewStyle>({
      web: {
        position: 'fixed' as any,
      },
      default: {
        position: 'absolute',
      },
    }) as ViewStyle),
    zIndex: 50,
    elevation: 400,
  },

  askbo: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    elevation: 400,
    ...(Platform.OS === 'web'
      ? ({
          touchAction: 'none',
          cursor: 'grab',
          userSelect: 'none',
        } as any)
      : {}),
  },

  circle: {
    width: ICON_SIZE,
    height: ICON_SIZE,
    borderRadius: ICON_SIZE / 2,
    overflow: 'hidden',
    borderWidth: 1,
    backgroundColor: getColor(pd.sys.colour.bg_primary),
    borderColor: getColor(pd.sys.colour.outline_low),
  },

  icon: {
    width: '100%',
    height: '100%',
  },
});
